import { useSyncExternalStore } from "react";

export type WishlistItem = {
  id: string;
  slug: string;
  name: string;
  price_cents: number;
  currency: string;
  image_url: string | null;
  added_at: number;
};

const STORAGE_KEY = "coderlity_wishlist_v1";
type Listener = () => void;
const listeners = new Set<Listener>();
let items: WishlistItem[] = load();

function load(): WishlistItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist() {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
}

function emit() {
  persist();
  listeners.forEach((l) => l());
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    items = load();
    listeners.forEach((l) => l());
  });
}

export const wishlistStore = {
  getSnapshot: () => items,
  subscribe(l: Listener) {
    listeners.add(l);
    return () => { listeners.delete(l); };
  },
  has(id: string) {
    return items.some((i) => i.id === id);
  },
  toggle(item: Omit<WishlistItem, "added_at">) {
    if (items.some((i) => i.id === item.id)) {
      items = items.filter((i) => i.id !== item.id);
    } else {
      items = [{ ...item, added_at: Date.now() }, ...items];
    }
    emit();
  },
  remove(id: string) {
    items = items.filter((i) => i.id !== id);
    emit();
  },
  clear() {
    items = [];
    emit();
  },
};

const EMPTY: WishlistItem[] = [];
function serverSnapshot() { return EMPTY; }

export function useWishlist() {
  return useSyncExternalStore(wishlistStore.subscribe, wishlistStore.getSnapshot, serverSnapshot);
}
